import allColoursImage from "@/assets/hero-atomizers.png";
import luxeLordsLogoWhite from "@/assets/luxe-lords-logo-white.svg";
import slide3 from "@/assets/hero-slide-3.jpg.asset.json";
import travelImage from "@/assets/hero-slide-travel.jpg";
import giftSet from "@/assets/hero-gift-set.jpg";
import { useEffect, useRef, useState } from "react";

// Add new hero photos here
const slides = [
  { src: allColoursImage, position: "center" },
  { src: giftSet, position: "center 62%" },
  { src: slide3.url, position: "center 77%" },
  { src: travelImage, position: "64% center" },
];

const Hero = () => {
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [fast, setFast] = useState(false);
  const [drag, setDrag] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setAnimate(true);
      setFast(false);
      setIndex((i) => i + 1);
    }, 6500);
    return () => clearInterval(t);
  }, [index]);

  // Seamless loop: after sliding onto the cloned first slide, snap back invisibly
  const handleEnd = () => {
    if (index >= slides.length) {
      setAnimate(false);
      setIndex(0);
    }
  };

  const go = (dir: number) => {
    setFast(true);
    if (dir > 0) {
      setAnimate(true);
      setIndex((i) => (i >= slides.length ? 1 : i + 1));
    } else if (index === 0) {
      // jump to the clone at the end, then slide back one
      setAnimate(false);
      setIndex(slides.length);
      requestAnimationFrame(() => requestAnimationFrame(() => { setAnimate(true); setIndex(slides.length - 1); }));
    } else {
      setAnimate(true);
      setIndex((i) => i - 1);
    }
  };

  const touch = useRef<{ x: number; y: number; horiz: boolean | null } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, horiz: null };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const t = touch.current;
    if (!t) return;
    const dx = e.touches[0].clientX - t.x;
    const dy = e.touches[0].clientY - t.y;
    if (t.horiz === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) t.horiz = Math.abs(dx) > Math.abs(dy);
    if (t.horiz) {
      setAnimate(false);
      // Finger-follow; resist past the first slide
      setDrag(index === 0 && dx > 0 ? dx * 0.35 : dx);
    }
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const t = touch.current;
    touch.current = null;
    if (!t) return;
    const dx = e.changedTouches[0].clientX - t.x;
    setDrag(0);
    if (t.horiz && Math.abs(dx) > 40) {
      if (dx < 0 || index > 0) go(dx < 0 ? 1 : -1);
      else { setFast(true); setAnimate(true); }
    } else { setFast(true); setAnimate(true); }
  };

  const track = [...slides, slides[0]];
  const active = index % slides.length;

  return (
    <section
      className="relative h-screen flex items-center justify-center overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{ touchAction: "pan-y" }}
    >
      {/* Full-screen background image */}
      <div
        aria-hidden="true"
        onTransitionEnd={handleEnd}
        className="absolute inset-0 flex"
        style={{
          transform: `translateX(calc(-${index * 100}% + ${drag}px))`,
          transition: animate ? (fast ? "transform 450ms cubic-bezier(0.22, 1, 0.36, 1)" : "transform 2600ms cubic-bezier(0.45, 0, 0.2, 1)") : "none",
          willChange: "transform",
        }}
      >
        {track.map((slide, i) => (
          <div
            key={i}
            className="relative h-full w-full shrink-0 bg-cover bg-no-repeat"
            style={{ backgroundImage: `url(${slide.src})`, backgroundPosition: slide.position }}
          />
        ))}
      </div>
      
      {/* Lighter overlay so the photos stay bright; soft centre shade keeps the logo readable */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/40 via-black/20 to-black/45 md:from-black/30 md:via-black/10 md:to-black/40" />
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,_rgba(0,0,0,0.4)_0%,_transparent_60%)] md:bg-[radial-gradient(ellipse_at_center,_rgba(0,0,0,0.3)_0%,_transparent_55%)]" />

      {/* Content - balanced vertical rhythm so the logo breathes above the CTAs */}
      <div className="relative z-10 text-center px-6 flex flex-col items-center">
        {/* Large Logo — slightly smaller on mobile to leave clear space above the tagline + CTAs */}
        <h1 className="relative inline-block mb-6 sm:mb-8 mx-auto animate-fade-in leading-[0] align-top">
          <span className="sr-only">Luxe Lords™ Premium Vegan Leather Perfume Atomisers</span>
          <img
            src={luxeLordsLogoWhite}
            alt=""
            aria-hidden="true"
            className="block w-[78vw] sm:w-[68vw] md:w-[58vw] lg:w-[50vw] xl:w-[44vw] max-w-[680px] md:max-w-[760px] lg:max-w-[840px] h-auto object-contain"
            style={{
              filter: 'drop-shadow(0 6px 40px rgba(0,0,0,0.75)) drop-shadow(0 2px 8px rgba(0,0,0,0.5))'
            }}
          />
          {/* Trademark — lifted above cap height, tucked close to the final letter */}
          <span
            aria-hidden="true"
            className="absolute text-white/90 font-sans font-medium leading-none select-none
                       text-[10px] sm:text-xs md:text-sm lg:text-base
                       -right-3 sm:-right-4 md:-right-5 lg:-right-6
                       -top-1 sm:-top-1.5 md:-top-2 lg:-top-2.5"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}
          >
            ™
          </span>
        </h1>
        
        <p 
          className="text-luxury text-white/90 mb-8 sm:mb-8 max-w-xl mx-auto animate-slide-up drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]"
          style={{ animationDelay: "0.2s" }}
        >
          Premium Vegan Leather Perfume Atomizers
        </p>
        
        <div
          className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4 animate-slide-up"
          style={{ animationDelay: "0.3s" }}
        >
          <a href="/shop" className="btn-luxury px-6 py-3 text-sm sm:text-base">
            Explore Collection
          </a>
          <a href="#craft" className="btn-luxury-outline border-white text-white hover:bg-white hover:text-foreground px-6 py-3 text-sm sm:text-base">
            Our Craft
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-fade-in" style={{ animationDelay: "1s" }}>
        <div className="flex flex-col items-center gap-2 text-white/80">
          <span className="text-luxury text-xs drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-white/80 to-transparent" />
        </div>
      </div>
      {/* Slide dots */}
      <div className="absolute bottom-28 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { setAnimate(true); setIndex(i); }}
            aria-label={`Show slide ${i + 1}`}
            aria-current={i === active}
            className="p-2 group"
          >
            <span
              className={`block h-2.5 w-2.5 rounded-full border border-primary-foreground shadow-[0_1px_4px_rgba(0,0,0,0.5)] transition-all duration-500 ${i === active ? "bg-primary-foreground scale-110" : "bg-transparent opacity-80 group-hover:bg-primary-foreground/60"}`}
            />
          </button>
        ))}
      </div>
    </section>
  );
};

export default Hero;
